import { Component, HostListener, OnInit } from '@angular/core';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { get, isArray, isEqual, isObject, set } from 'lodash-es';
import { environment } from '../environments/environment.active';
import { select, Store } from '@ngrx/store';
import { RootState } from './store/store.state';
import { AddAlert } from './alerts/store/alerts.action';

import { selectAuthClaims } from './auth/store/auth.store';
import { AnalyticsService } from './singleton-services/services/analytics.service';
import {
    AnalyticsEvent,
    RoutePageNameMap
} from '../../common/models/app-config.interface';
import { NavigationEnd, Router } from '@angular/router';
import { BaseAbstractComponent } from './shared/base/base-abstract.component';
import { GlobalDomEventService } from './singleton-services/services/global-dom-event.service';
import { AlertSeverity } from '../../common/models/alert.interface';

@Component({
    standalone: false,
    selector: 'app-root',
    templateUrl: './app.component.html'
})
export class AppComponent extends BaseAbstractComponent implements OnInit {
    authenticateComplete: boolean = false;

    constructor(
        private store: Store<RootState>,
        private globalDomEventService: GlobalDomEventService,
        private analyticsService: AnalyticsService /* Injecting AnalyticsService to load Google Analytics script */,
        private router: Router
    ) {
        super();
        /* To be notified once authentication is complete, so that header & footer component can be modified */
        this.store
            .pipe(
                select(selectAuthClaims),
                filter(
                    (claims) =>
                        isObject(claims) && isArray(claims.enabled_sso_apps)
                ),
                takeUntil(this.$destroy)
            )
            .subscribe((claims) => {
                this.authenticateComplete = true;

                /* Extract logged in user's email domain from claims for GA tracking */
                const emailDomain = claims.email?.split('@')[1];
                if (emailDomain) {
                    this.analyticsService.trackEvent(AnalyticsEvent.UserLogin, {
                        logged_in_user_email_domain: emailDomain
                    });
                }
            });
    }

    ngOnInit() {
        console.log(
            'Shopper Support App loaded with environment settings',
            environment
        );

        this.router.events
            .pipe(takeUntil(this.$destroy))
            .pipe(filter((event) => event instanceof NavigationEnd))
            .pipe(debounceTime(1000))
            .subscribe((event: NavigationEnd) => {
                this.analyticsService.trackEvent(AnalyticsEvent.NavigatedPage, {
                    page_title: this.getRoutePageName(event.urlAfterRedirects)
                });
            });
    }

    getRoutePageName(url: string): string {
        const routeKeys = Object.keys(RoutePageNameMap) ?? [];
        for (const key of routeKeys) {
            if (url.startsWith(key) || url.includes(key)) {
                return RoutePageNameMap[key];
            }
        }
        return `Page URL: ${url}`; // Return the URL if no match is found
    }

    /**
     * This function returns an updated version of the given state, by updating it with the data from
     * the stored state (could be from browser's local storage). The updates are done only to the
     * paths mentioned in the storedPropertyList
     * @param currentStateSchema: object which has to be modified and returned
     * @param storedStateSchema: object which is a copy of the state from a previous version of app.
     * @param storedState: object that will be used to update the given state.
     * @param storedPropertyList: A list of dot separated strings, that represent a path to a property in the state.
     */
    updateStateFromLocalStorage<T extends object>(
        currentStateSchema: T,
        storedStateSchema: any,
        storedState: any,
        storedPropertyList: string[]
    ): T {
        let alertShown = false;
        storedPropertyList.forEach((path: string) => {
            if (
                get(storedStateSchema, path) !== undefined &&
                isEqual(
                    get(currentStateSchema, path),
                    get(storedStateSchema, path)
                )
            ) {
                set(currentStateSchema, path, get(storedState, path));
            } else if (!alertShown) {
                alertShown = true;
                this.store.dispatch(
                    new AddAlert({
                        message: 'Some of the filter settings have been reset',
                        severity: AlertSeverity.INFO
                    })
                );
            }
        });
        return currentStateSchema;
    }

    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        this.globalDomEventService.documentClickSubject.next(event);
    }

    preOnDestroy() {}
}
